import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import VendorEditor from '../../VendorsPage/VendorEditor/VendorEditor';
import { objectHelpers } from '../../../../utils/helpers';

const WithVendorBankingCheck = ({
  checkVendorBankingRequired,
  ModalComponent,
  modalProps,
}) => {
  const [isOpenVendorEditor, setIsOpenVendorEditor] = useState(false);
  const [payeeData, setPayeeData] = useState({ vendor: null, beneficiary: null });
  const [t] = useTranslation('main');

  const vendorSelectorRef = useRef(null);

  const additionalModalProps = useMemo(() => {
    const checkBanking =
      typeof checkVendorBankingRequired === 'function'
        ? checkVendorBankingRequired
        : ({ vendor, beneficiary }) => vendor && !beneficiary;

    if (checkBanking(payeeData)) {
      return {
        okText: t('addBankingDetails'),
        onOk: () => setIsOpenVendorEditor(true),
      };
    }

    return {};
  }, [checkVendorBankingRequired, payeeData, t]);

  const handleBeneficiaryChange = ({ vendor: newVendor, beneficiary: newBeneficiary }) => {
    const vendorChanged = !objectHelpers.arraysIsEqual(newVendor, payeeData.vendor);
    const beneficiaryChanged = newBeneficiary !== payeeData.beneficiary;

    if (!vendorChanged && !beneficiaryChanged) return;

    setPayeeData({
      vendor: vendorChanged ? newVendor : payeeData.vendor,
      beneficiary: beneficiaryChanged ? newBeneficiary : payeeData.beneficiary,
    });
  };

  const handleCloseVendorEditor = () => setIsOpenVendorEditor(false);

  const handleSuccessVendorEdit = () => {
    handleCloseVendorEditor();
    if (vendorSelectorRef.current) {
      vendorSelectorRef.current.loadVendors(payeeData?.vendor?.vendor_id);
    }
  };

  return (
    <>
      <ModalComponent
        {...modalProps}
        {...additionalModalProps}
        vendorSelectorProps={{
          onBeneficiaryChange: handleBeneficiaryChange,
          ref: vendorSelectorRef
        }}
      />

      <VendorEditor
        mode='edit'
        open={isOpenVendorEditor}
        onCancel={handleCloseVendorEditor}
        onSuccess={handleSuccessVendorEdit}
        size='banking'
        vendorId={payeeData?.vendor?.vendor_id}
      />
    </>
  );
};

WithVendorBankingCheck.propTypes = {
  checkVendorBankingRequired: PropTypes.func,
  ModalComponent: PropTypes.elementType.isRequired,
  modalProps: PropTypes.object.isRequired,
};

export default WithVendorBankingCheck;
